import type { MouseEvent, ReactElement } from 'react';
import { useEffect, useRef } from 'react';

import { BREAKPOINTS } from '@skyscanner-internal/backpack-web/bpk-component-breakpoint';
import {
  BpkBox,
  BpkFlex,
  BpkSpacing,
  BpkVessel,
} from '@skyscanner-internal/backpack-web/bpk-component-layout';
import { useEverywhereSearchNavigation } from '@web-platform/shared-banana-redux/src/helpers/navigationHandlers/useEverywhereSearchNavigation';
import { useGlobalNavData } from '@web-platform/shared-banana-redux/src/network-layer/hooks/useGlobalNavData';
import { allNavCardAdverts } from '@web-platform/shared-banana-redux/src/state/combinedResults';
import { useMediaQuery } from '@web-platform/shared-banana-utils-frontend/src/helpers/hooks/useMediaQuery/useMediaQuery';
import { ANALYTICS_EVENTS } from '@web-platform/shared-universal-constants-tracking';
import {
  trackChokePoint,
  trackSmartMetric,
} from '@web-platform/shared-universal-tracking/src/trackExperiment';
import { trackInteractionEvent } from '@web-platform/shared-universal-tracking/src/trackInteractionEvent/trackInteractionEvent';
import ErrorBoundary from '@web-platform/shared-universal-ui-error-boundary';
import { MobileScrollContainer } from '@web-platform/shared-universal-ui-mobile-scroll-container';
import { logWarn } from '@web-platform/shared-universal-utils-frontend/src/helpers/logger';
import { useSelector } from 'react-redux';
import { GLOBAL } from 'saddlebag-browser';
import { logOperationalEvent } from 'saddlebag-logger';

import { GlobalNavigationFallback } from '../GlobalNavigationFallback/GlobalNavigationFallback';

import { GlobalNavigationCard } from './GlobalNavigationCard/GlobalNavigationCard';
import { GlobalNavigationEmptyCard } from './GlobalNavigationEmptyCard/GlobalNavigationEmptyCard';

import type { TrackingContext } from '@skyscanner/strevda-tracking';
import type { InteractionEventName } from '@web-platform/shared-universal-types-tracking/src/trackInteractionEvent/trackInteractionEvent';

import STYLES from './GlobalNavigation.module.scss';

export type defaultTranslationsType = {
  headline: string;
  ctaUrl: string;
};

export type Props = {
  parentPage: string;
};

const STATEFUL_NAV_CARDS = {
  EXPLORE_EVERYWHERE: 'EXPLORE_EVERYWHERE',
};

const GLOBAL_NAV_RESPONSE_FAILURE = {
  GLOBAL_NAV_RESPONSE_ERROR: 'GLOBAL_NAV_RESPONSE_ERROR',
  GLOBAL_NAV_RESPONSE_TIMEOUT: 'GLOBAL_NAV_RESPONSE_TIMEOUT',
  GLOBAL_NAV_RESPONSE_NONE_AVAILABLE: 'GLOBAL_NAV_RESPONSE_NONE_AVAILABLE',
};

// https://partnerportal.skyscanner.net/strevda/campaign/2902/lineitem/CAM-86c7fe10-45bd-4c03-b0d9-0f72b09e2d2c
const COMBINED_EXPLORE_EXPERIMENT_B_CAMPAIGN_ID = 'CAM-86c7fe10-45bd-4c03-b0d9-0f72b09e2d2c';

export const sortGlobalNavigationCards = (navCardsKeys: string[]): string[] =>
  navCardsKeys.sort((a, b) => a.localeCompare(b));

const isExploreEverywhereSearch = (link: string) => link === STATEFUL_NAV_CARDS.EXPLORE_EVERYWHERE;

const getHref = (link: string) => {
  if (isExploreEverywhereSearch(link)) {
    return '#';
  }
  return link;
};

const globalNavigationFailure = async (reason: string): Promise<void> => {
  await trackInteractionEvent({
    name: ANALYTICS_EVENTS.INTERACTION.GLOBAL_NAV_RESPONSE_FAILURE.NAME,
    strings: {
      reason,
    },
  });
};

export const GlobalNavigation = ({ parentPage }: Props): ReactElement => {
  const navCards = useSelector(allNavCardAdverts);

  const navCardsKeys = sortGlobalNavigationCards(Object.keys(navCards));

  const { moreDataExpected, timedOut } = useGlobalNavData();

  const { handleEverywhereSearchNavigation } = useEverywhereSearchNavigation({
    tracking: { trackInteractionEvent },
  });

  const trackNavCardInteraction = (
    name: InteractionEventName,
    translatedFields: defaultTranslationsType,
    position: string,
    link: string,
  ): void => {
    const payload = {
      CTAUrl: translatedFields.ctaUrl,
      headline: translatedFields.headline,
      label: translatedFields.headline,
      navSlot: position,
      link,
    };

    trackInteractionEvent({
      name,
      strings: {
        ...payload,
      },
    });
    logOperationalEvent({
      eventName: name,
      component: 'GlobalNavigationCard',
      ...payload,
    });
  };

  const onGlobalNavCardClicked = async (
    link: string,
    translatedFields: defaultTranslationsType,
    position: string,
    event: MouseEvent<Element>,
    analyticsProperties: TrackingContext,
  ): Promise<void> => {
    trackNavCardInteraction(
      ANALYTICS_EVENTS.INTERACTION.GLOBAL_NAV_CARD_CLICKED.NAME,
      translatedFields,
      position,
      link,
    );

    if (
      isExploreEverywhereSearch(link) ||
      analyticsProperties.idCampaign === COMBINED_EXPLORE_EXPERIMENT_B_CAMPAIGN_ID
    ) {
      trackChokePoint('explore_everywhere_navcard_clicked');
      trackSmartMetric('HOMEPAGE_EE_NAV_CARD_CLICKED');
    }

    if (isExploreEverywhereSearch(link)) {
      GLOBAL.getWindow().scrollTo(0, 0);
      // needed as BpkCard uses href to render <a> and it causes the page to reload, so this prevents it (GlobalNavigation component is using GlobalNavigationCard which uses BpkCard)
      event.preventDefault();
      handleEverywhereSearchNavigation();
    }
  };

  const navCardsNoneAvailable = !moreDataExpected && !navCards.isError && navCardsKeys.length === 0;

  const showFallback = navCardsNoneAvailable || navCards.isError || timedOut;

  const failureEmitted = useRef(false);
  useEffect(() => {
    if (failureEmitted.current) return;
    if (navCards.isError) {
      failureEmitted.current = true;
      globalNavigationFailure(GLOBAL_NAV_RESPONSE_FAILURE.GLOBAL_NAV_RESPONSE_ERROR);
    } else if (timedOut) {
      failureEmitted.current = true;
      globalNavigationFailure(GLOBAL_NAV_RESPONSE_FAILURE.GLOBAL_NAV_RESPONSE_TIMEOUT);
    } else if (navCardsNoneAvailable) {
      failureEmitted.current = true;
      globalNavigationFailure(GLOBAL_NAV_RESPONSE_FAILURE.GLOBAL_NAV_RESPONSE_NONE_AVAILABLE);
    }
  }, [navCards.isError, timedOut, navCardsNoneAvailable]);

  const isSmallScreen = useMediaQuery(BREAKPOINTS.TABLET);
  const isSponsoredCardPresent = navCardsKeys.some(
    (key: string | number) => navCards[key]?.creative?.fields?.sponsored,
  );
  const renderGlobalNavigationCardsContainer = () => (
    <BpkFlex
      data-testid="GlobalNavigation-cardsContainer"
      marginInline="auto"
      columnGap={{ base: BpkSpacing.MD, tablet: BpkSpacing.Base }}
      minHeight={isSponsoredCardPresent ? { base: '8rem', tablet: '6.75rem' } : undefined}
      maxWidth={{ tablet: '79.5rem' }}
    >
      {moreDataExpected && navCardsKeys.length === 0 && (
        <>
          <GlobalNavigationEmptyCard />
          <GlobalNavigationEmptyCard />
          <GlobalNavigationEmptyCard />
          <GlobalNavigationEmptyCard />
        </>
      )}
      {!showFallback &&
        navCardsKeys.map((key) => {
          const { analyticsProperties, defaultTranslations, performanceEventsPayloads, pixels } =
            navCards[key];

          const { backgroundImage, ctaUrl, headline, partnerName, sponsored } =
            navCards[key].creative.fields;

          return (
            <ErrorBoundary
              componentName="NavCard"
              fallback={null}
              onError={({ component, error }) =>
                logWarn({
                  component,
                  error,
                  message: `A data error occurred while rendering the Global Navigation Card: ${key}.`,
                })
              }
              key={key}
            >
              <GlobalNavigationCard
                key={key}
                position={key}
                title={headline}
                image={backgroundImage}
                isSponsored={sponsored}
                isSponsoredCardPresent={isSponsoredCardPresent}
                partnerName={partnerName}
                link={getHref(ctaUrl)}
                onClick={(e) =>
                  onGlobalNavCardClicked(ctaUrl, defaultTranslations, key, e, analyticsProperties)
                }
                defaultTranslations={defaultTranslations}
                performanceEventsPayloads={performanceEventsPayloads}
                pixels={pixels}
                analyticsProperties={analyticsProperties}
              />
            </ErrorBoundary>
          );
        })}
    </BpkFlex>
  );

  return (
    <BpkBox>
      {showFallback ? (
        <ErrorBoundary
          componentName="FallbackNavCard"
          fallback={null}
          onError={({ component, error }) =>
            logWarn({
              component,
              error,
              message: 'A data error occurred while rendering the Global Navigation Fallback Card.',
            })
          }
        >
          <GlobalNavigationFallback parentPage={parentPage} />
        </ErrorBoundary>
      ) : (
        // TODO: CLOV-1913 — BpkVessel retained: negative inline margins unsupported by Backpack layout props.
        <BpkVessel className={STYLES.container}>
          {isSmallScreen ? (
            <MobileScrollContainer>{renderGlobalNavigationCardsContainer()}</MobileScrollContainer>
          ) : (
            renderGlobalNavigationCardsContainer()
          )}
        </BpkVessel>
      )}
    </BpkBox>
  );
};
